import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ListChecks, Building2, Mail, Calendar, User, AlertCircle, Loader2, MessageSquare } from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import superAdminAuditService from '../../../../../services/audit_services/superAdminAuditService';

export default function AllAuditSectionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sectionData, setSectionData] = useState(null);

  useEffect(() => {
    if (id) {
      fetchSectionDetails();
    }
  }, [id]);

  const fetchSectionDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await superAdminAuditService.getAuditSectionDetails(authTokens.access, id);
      setSectionData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="ml-2 text-sm text-red-700">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!sectionData) {
    return (
      <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
        <div className="text-center py-12">
          <p className="text-gray-500">Aucune donnée trouvée</p>
        </div>
      </div>
    );
  }

  const { section, organization, audit_plan, created_by, last_updated_by, questions, statistics } = sectionData;

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/admin-panel/audit-sections')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{section?.section_name}</h1>
            <p className="text-xs text-gray-500">Détails de la section d'audit</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Section Information */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <ListChecks className="h-5 w-5 text-gray-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900">Informations de la section</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Nom de la section</label>
                <p className="text-sm text-gray-900">{section?.section_name || 'N/A'}</p>
              </div>
              {section?.location && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Localisation</label>
                  <p className="text-sm text-gray-900">{section.location}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              {section?.created_at && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Créé le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(section.created_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              )}
              {section?.updated_at && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Mis à jour le</label>
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-sm text-gray-900">
                      {new Date(section.updated_at).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Organization */}
        {organization && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-900">{organization.name}</p>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded ${
                  organization.is_subscribed 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {organization.is_subscribed ? 'Abonné' : 'Non abonné'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Audit Plan */}
        {audit_plan && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <ListChecks className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Plan d'audit</h2>
            </div>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-900">{audit_plan.title}</p>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs rounded ${
                  audit_plan.status === 'achieved' ? 'bg-green-100 text-green-700' :
                  audit_plan.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  audit_plan.status === 'completed' ? 'bg-purple-100 text-purple-700' :
                  audit_plan.status === 'closed' ? 'bg-gray-100 text-gray-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {audit_plan.status}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Created By & Last Updated By */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {created_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Créé par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">{created_by.full_name || 'Inconnu'}</p>
                {created_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{created_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {last_updated_by && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Dernière modification par</h2>
              </div>
              
              <div className="space-y-2">
                <p className="text-sm text-gray-900">{last_updated_by.full_name || 'Inconnu'}</p>
                {last_updated_by.email && (
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 text-gray-400 mr-1" />
                    <p className="text-xs text-gray-600">{last_updated_by.email}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Questions */}
        {questions && questions.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <MessageSquare className="h-5 w-5 text-gray-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900">Questions d'audit</h2>
              </div>
              <div className="text-xs text-gray-500">
                {statistics?.total_questions || questions.length} question(s)
              </div>
            </div>

            <div className="space-y-3">
              {questions.map((question) => (
                <div key={question.id} className="p-4 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm font-medium text-gray-900">{question.question_name}</p>
                    <span className={`px-2 py-1 text-xs rounded ${
                      question.status === 'completed' ? 'bg-green-100 text-green-700' :
                      question.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {question.status}
                    </span>
                  </div>
                  {question.location && (
                    <p className="text-xs text-gray-600 mb-2">Localisation: {question.location}</p>
                  )}
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span>Créé le {new Date(question.created_at).toLocaleDateString('fr-FR')}</span>
                    {question.created_by && (
                      <>
                        <span>•</span>
                        <span>Par {question.created_by.name || question.created_by.email}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statistics */}
        {statistics && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <ListChecks className="h-5 w-5 text-gray-600 mr-2" />
              <h2 className="text-sm font-semibold text-gray-900">Statistiques</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{statistics.total_questions}</p>
                <p className="text-xs text-gray-600">Total questions</p>
              </div>
              {statistics.questions_by_status && (
                <>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-700">{statistics.questions_by_status.pending || 0}</p>
                    <p className="text-xs text-yellow-700">En attente</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">{statistics.questions_by_status.in_progress || 0}</p>
                    <p className="text-xs text-blue-700">En cours</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{statistics.questions_by_status.completed || 0}</p>
                    <p className="text-xs text-green-700">Complétées</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
